import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { FlowScenarioName, LinkedDropDownObject, RecommendedProduct, WebFlowItem } from './types';
import { ToastIntent } from '@fluentui/react-components';
import { secondaryAuth } from './secondaryFirebaseSetup';
import { dispatchNotification } from './Context/Notification/types';
import { InnateQualities, ReportProps } from '@leelalife/report';
import { RoleType } from './Components/RoleManager/RoleManager';

let access_token: string | undefined = undefined;
let secondary_access_token: string | undefined = undefined;

export function hasDevModeOverrideInURL(): boolean {
  return window.location.href.includes('devMode=true');
}

const hasDevOverrideForFunction = localStorage.getItem('devmode') === 'true';

// New function to check environment from our context or fallback to old methods
function isDevEnvironment(): boolean {
  // Check new environment localStorage key first
  const savedEnv = localStorage.getItem('leelalife-environment');
  if (savedEnv) {
    return savedEnv === 'development';
  }

  // Fallback to old methods for backward compatibility
  return hasDevModeOverrideInURL() || hasDevOverrideForFunction;
}

function getURLFromFunctionName(name: string): string {
  const productionName = name.replace(/_/g, '-');
  return isDevEnvironment()
    ? `http://127.0.0.1:5001/subtle-cubist-419805/us-central1/${name}`
    : `https://${productionName}-gqjfwgmboa-uc.a.run.app`;
}

// Export function for getting Firebase function URLs based on environment
export function getFirebaseFunctionURL(functionName: string): string {
  return getURLFromFunctionName(functionName);
}

// Export the environment check function for use in other parts of the application
export function getCurrentEnvironment(): 'development' | 'production' {
  return isDevEnvironment() ? 'development' : 'production';
}

export async function refreshAuthToken(): Promise<string | undefined> {
  access_token = await getAuth().currentUser?.getIdToken();

  return access_token;
}

export async function refreshSecondaryAuthToken(): Promise<string | undefined> {
  secondary_access_token = await secondaryAuth.currentUser?.getIdToken();

  return secondary_access_token;
}

function isTokenExpired(token: string) {
  const decodedToken = JSON.parse(atob(token.split('.')[1]));
  return decodedToken.exp < Date.now() / 1000;
}

export async function uploadToCDNFromImageURL(url: string, fileName: string) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    alert(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com"
    );
  }
  return Promise.resolve('https://cdn.com/image.jpg');
}

export async function addRecommendedItemToCollection(
  collectionId: string,
  item: RecommendedProduct
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    alert(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com"
    );
  }

  const response = await axios.post(
    // "http://127.0.0.1:5001/subtle-cubist-419805/us-central1/handleWebflow",
    'https://handlewebflow-gqjfwgmboa-uc.a.run.app',
    {
      actionType: 'ADD_ITEM_TO_COLLECTION',
      request: {
        collectionId,
        isDraft: true,
        fieldData: item,
      },
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    alert(`Error adding item to collection ${response}`);
  }

  return response.data;
}

export async function fetchRecommendedItemFromCollection(
  collectionId: string,
  itemId: string
): Promise<WebFlowItem<'RecommendedProduct'>> {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    alert(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com"
    );
  }

  const response = await axios.post(
    // "http://127.0.0.1:5001/subtle-cubist-419805/us-central1/handleWebflow",
    'https://handlewebflow-gqjfwgmboa-uc.a.run.app',
    {
      actionType: 'FETCH_ITEM_FROM_COLLECTION',
      request: {
        collectionId,
        itemId,
      },
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    alert(`Error fetching item from collection ${response}`);
  }

  const fetchedItem: WebFlowItem<'RecommendedProduct'> = {
    isDraft: response.data.isDraft,
    isArchived: response.data.isArchived,
    fieldData: {
      amazonUrl: response.data.fieldData['amazon-url'],
      brand: response.data.fieldData.brand,
      name: response.data.fieldData.name,
      productImageLink: response.data.fieldData['product-image-link'],
      slug: response.data.fieldData.slug,
    },
  };

  return fetchedItem;
}

export async function fetchExperiencesItemFromCollection(
  collectionId: string,
  itemId: string
): Promise<WebFlowItem<'Experience'>> {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    alert(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com"
    );
  }

  const response = await axios.post(
    // "http://127.0.0.1:5001/subtle-cubist-419805/us-central1/handleWebflow",
    'https://handlewebflow-gqjfwgmboa-uc.a.run.app',
    {
      actionType: 'FETCH_ITEM_FROM_COLLECTION',
      request: {
        collectionId,
        itemId,
      },
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    alert(`Error fetching item from collection ${response}`);
  }

  const fetchedItem: WebFlowItem<'Experience'> = {
    isDraft: response.data.isDraft,
    isArchived: response.data.isArchived,
    id: response.data.id,
    fieldData: {
      name: response.data.fieldData.name,
      slug: response.data.fieldData.slug,
      aboutLearning: response.data.fieldData['what-you-will-learn'],
      destinationAboutInstitution: response.data.fieldData['details'],
      relevanceAncient: response.data.fieldData['relevance-ancient'],
      relevanceScientific: response.data.fieldData['relevance-scientific'],
      contextTimeAndSeason: response.data.fieldData['context---time-season'],
      locationText: response.data.fieldData['location-text'],
      experienceByName: response.data.fieldData['offering'],
      experienceByCrispBio: response.data.fieldData['crisp-bio'],
      offeringFeatureImage: {
        fileId: response.data.fieldData['offering-feature-image'].fileId,
        url: response.data.fieldData['offering-feature-image'].url,
        alt: response.data.fieldData['offering-feature-image'].alt,
      },
      //   images: response.data.fieldData.images.map((image: any) => {
      //     console.log("image", image);
      //     return {
      //       fileId: image.fileId,
      //       url: image.url,
      //       alt: image.alt,
      //     };
      //   }),
      //   offeringFeatureImage: {
      //     fileId: response.data.fieldData["offering-feature-image"].fileId,
      //     url: response.data.fieldData["offering-feature-image"].url,
      //     alt: response.data.fieldData["offering-feature-image"].alt,
      //   },
      //   experienceByImage: {
      //     fileId: response.data.fieldData["specialist-photo"].fileId,
      //     url: response.data.fieldData["specialist-photo"].url,
      //     alt: response.data.fieldData["specialist-photo"].alt,
      //   },
      aboutSpecialist: response.data.fieldData['about--specialist'],
      offeringLongDescription: response.data.fieldData['offering-long-description'],
      sessionFullPlan: response.data.fieldData['session-full-plan'],
      aboutBenefits: response.data.fieldData['practice-benefits'],
      experienceFullPrice: response.data.fieldData['offering-full-course-price'],
      experienceDuration: response.data.fieldData['session-duration'],
      aboutDailyLife: response.data.fieldData['incorporating-in-daily-life'],
      destinationPlace: response.data.fieldData['destination---place'],
      experinceUpcoming: response.data.fieldData['booking-info-2'],
      calendarId: response.data.fieldData['calendar-id'],
    },
  };

  return fetchedItem;
}

export async function fetchItemDetailsFromCollection(
  collectionId: string,
  itemId: string,
  dispatchNotification: dispatchNotification
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    alert(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com"
    );
  }

  const response = await axios.post(
    // "http://127.0.0.1:5001/subtle-cubist-419805/us-central1/handleWebflow",
    'https://handlewebflow-gqjfwgmboa-uc.a.run.app',
    {
      actionType: 'FETCH_ITEM_FROM_COLLECTION',
      request: {
        collectionId,
        itemId,
      },
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error fetching item from collection ${response}`, 'error');
  }

  dispatchNotification('Item fetched successfully', 'success', 5000);

  return response.data;
}

export async function updateExperienceItemInCollection(
  collectionId: string,
  itemId: string,
  item: WebFlowItem<'Experience'>,
  onlyUpdateInSpreadsheet: boolean = false,
  dispatchNotification: (message: string, type: ToastIntent) => void
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    alert(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com"
    );
  }

  const response = onlyUpdateInSpreadsheet
    ? await Promise.resolve({ status: 200, data: {} })
    : await axios.post(
        // "http://127.0.0.1:5001/subtle-cubist-419805/us-central1/handleWebflow",
        'https://handlewebflow-gqjfwgmboa-uc.a.run.app',
        {
          actionType: 'UPDATE_ITEM_IN_COLLECTION',
          request: {
            collectionId,
            itemId,
            fieldData: {
              name: item.fieldData.name,
              slug: item.fieldData.slug,
              'what-you-will-learn': `${item.fieldData.aboutLearning}`,
              details: item.fieldData.destinationAboutInstitution,
              'relevance-ancient': item.fieldData.relevanceAncient,
              'relevance-scientific': item.fieldData.relevanceScientific,
              'context---time-season': item.fieldData.contextTimeAndSeason,
              'location-text': item.fieldData.locationText,
              offering: item.fieldData.experienceByName,
              'crisp-bio': item.fieldData.experienceByCrispBio,
              //   images: item.fieldData.images.map((image) => {
              //     return {
              //       fileId: image.fileId,
              //       url: image.url,
              //       alt: image.alt,
              //     };
              //   }),
              //   "offering-feature-image": {
              //     fileId: item.fieldData.offeringFeatureImage.fileId,
              //     url: item.fieldData.offeringFeatureImage.url,
              //     alt: item.fieldData.offeringFeatureImage.alt,
              //   },
              //   "specialist-photo": {
              //     fileId: item.fieldData.experienceByImage.fileId,
              //     url: item.fieldData.experienceByImage.url,
              //     alt: item.fieldData.experienceByImage.alt,
              //   },
              'about--specialist': item.fieldData.aboutSpecialist,
              'offering-long-description': item.fieldData.offeringLongDescription,
              'session-full-plan': item.fieldData.sessionFullPlan,
              'practice-benefits': item.fieldData.aboutBenefits,
              'offering-full-course-price': item.fieldData.experienceFullPrice,
              'session-duration': item.fieldData.experienceDuration,
              'incorporating-in-daily-life': item.fieldData.aboutDailyLife,
              'destination---place': item.fieldData.destinationPlace,
              'booking-info-2': item.fieldData.experinceUpcoming,
              'calendar-id': item.fieldData.calendarId,
            },
          },
        },
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      );

  if (response.status !== 200) {
    alert(`Error updating item in collection ${response}`);
  }

  //   alert("update to webflow successfull");

  dispatchNotification(
    onlyUpdateInSpreadsheet
      ? 'Skipped update in webflow'
      : 'Experience updated successfully to webflow',
    onlyUpdateInSpreadsheet ? 'info' : 'success'
  );

  const spreadSheetUpdateResponse = await updateExperiencesSpreadsheetItem(
    collectionId,
    itemId,
    item,
    dispatchNotification
  );
  if (spreadSheetUpdateResponse.status !== 200) {
    dispatchNotification('Error updating item in spreadsheet', 'error');
    return response.data;
  }
  return response.data;
}

export async function addExperienceItemInCollection(
  collectionId: string,
  item: WebFlowItem<'Experience'>
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    alert(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com"
    );
  }

  const response = await axios.post(
    // "http://127.0.0.1:5001/subtle-cubist-419805/us-central1/handleWebflow",
    'https://handlewebflow-gqjfwgmboa-uc.a.run.app',
    {
      actionType: 'ADD_ITEM_TO_COLLECTION',
      request: {
        collectionId: collectionId,
        isDraft: true,
        fieldData: {
          name: item.fieldData.name,
          slug: item.fieldData.slug,
          'what-you-will-learn': `${item.fieldData.aboutLearning}`,
          details: item.fieldData.destinationAboutInstitution,
          'relevance-ancient': item.fieldData.relevanceAncient,
          'relevance-scientific': item.fieldData.relevanceScientific,
          'context---time-season': item.fieldData.contextTimeAndSeason,
          'location-text': item.fieldData.locationText,
          offering: item.fieldData.experienceByName,
          'crisp-bio': item.fieldData.experienceByCrispBio,
          //   images: item.fieldData.images.map((image) => {
          //     return {
          //       fileId: image.fileId,
          //       url: image.url,
          //       alt: image.alt,
          //     };
          //   }),
          //   "offering-feature-image": {
          //     fileId: item.fieldData.offeringFeatureImage.fileId,
          //     url: item.fieldData.offeringFeatureImage.url,
          //     alt: item.fieldData.offeringFeatureImage.alt,
          //   },
          //   "specialist-photo": {
          //     fileId: item.fieldData.experienceByImage.fileId,
          //     url: item.fieldData.experienceByImage.url,
          //     alt: item.fieldData.experienceByImage.alt,
          //   },
          'about--specialist': item.fieldData.aboutSpecialist,
          'offering-long-description': item.fieldData.offeringLongDescription,
          'session-full-plan': item.fieldData.sessionFullPlan,
          'practice-benefits': item.fieldData.aboutBenefits,
          'offering-full-course-price': item.fieldData.experienceFullPrice,
          'session-duration': item.fieldData.experienceDuration,
          'incorporating-in-daily-life': item.fieldData.aboutDailyLife,
          'destination---place': item.fieldData.destinationPlace,
          'booking-info-2': item.fieldData.experinceUpcoming,
          'calendar-id': item.fieldData.calendarId,
        },
      },
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    alert(`Error adding item to collection ${response}`);
  }

  return response.data;
}

export async function fetchSpecialistItemFromCollection(
  collectionId: string,
  itemId: string
): Promise<WebFlowItem<'Specialist'>> {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    alert(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com"
    );
  }

  const response = await axios.post(
    // http://127.0.0.1:5001/subtle-cubist-419805/us-central1/handleWebflow",
    'https://handlewebflow-gqjfwgmboa-uc.a.run.app',
    {
      actionType: 'FETCH_ITEM_FROM_COLLECTION',
      request: {
        collectionId,
        itemId,
      },
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    alert(`Error fetching item from collection ${response}`);
  }

  const fetchedItem: WebFlowItem<'Specialist'> = {
    isDraft: response.data.isDraft,
    isArchived: response.data.isArchived,
    id: response.data.id,
    fieldData: {
      name: response.data.fieldData.name,
      slug: response.data.fieldData.slug,
      crispBio: response.data.fieldData['crisp-bio'],
      ranking: response.data.fieldData.ranking,
      bio: response.data.fieldData.details,
      locationText: response.data.fieldData['location-text'],
      offeringId: response.data.fieldData['offering-id'],
      offeringStatus: response.data.fieldData['offering-status'],
      offeringType: response.data.fieldData['type-of-offering'],
      offeringName: response.data.fieldData['offering'],
      offeringLongDescription: response.data.fieldData['offering-long-description'],
      offeringShortDescription: response.data.fieldData['offering-short-description'],
      offeringWellbeing: response.data.fieldData['wellbeing-capacity'],
      aboutLearning: response.data.fieldData['what-you-will-learn'],
      aboutBenefits: response.data.fieldData['practice-benefits'],
      aboutDailyLife: response.data.fieldData['incorporating-in-daily-life'],
      relevanceAncient: response.data.fieldData['relevance-ancient'],
      relevanceScientific: response.data.fieldData['relevance-scientific'],
      introSessionExplainer: response.data.fieldData['session-introductory'],
      introSessionPrerequisites: response.data.fieldData['intro-session-prerequisites'],
      sessionFullPlan: response.data.fieldData['session-full-plan'],
      sessionType: response.data.fieldData['session-type'],
      sessionPrivateSessions: response.data.fieldData['booking-info-1'],
      sessionFrequency: response.data.fieldData['booking-info-2'],
      sessionDuration: response.data.fieldData['session-duration'],
      faqs: response.data.fieldData['faqs'],
      price: response.data.fieldData['price'],
      offeringTypeConduct: response.data.fieldData['offering-type-conduct'],
      offeringFullCoursePrice: response.data.fieldData['offering-full-course-price'],
      calendarId: response.data.fieldData['calendar-id'],
      ownerId: response.data.fieldData['owner-id'],
      offeringCapacityName: response.data.fieldData['offering-capacity-name'],
      elements: response.data.fieldData.elements || [],
      subElements: response.data.fieldData['sub-elements'] || [],
      mainColor: response.data.fieldData['main-color'],
    },
  };

  return fetchedItem;
}

export async function updateSpecialistItemInCollection(
  collectionId: string,
  itemId: string,
  item: WebFlowItem<'Specialist'>,
  onlyUpdateInSpreadsheet: boolean = false,
  dispatchNotification: (message: string, type: ToastIntent) => void
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    alert(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com"
    );
  }

  const response = onlyUpdateInSpreadsheet
    ? await Promise.resolve({ status: 200, data: {} })
    : await axios.post(
        // "http://127.0.0.1:5001/subtle-cubist-419805/us-central1/handleWebflow",
        'https://handlewebflow-gqjfwgmboa-uc.a.run.app',
        {
          actionType: 'UPDATE_ITEM_IN_COLLECTION',
          request: {
            collectionId,
            itemId,
            fieldData: {
              name: item.fieldData.name,
              slug: item.fieldData.slug,
              'crisp-bio': item.fieldData.crispBio,
              ranking: item.fieldData.ranking,
              details: item.fieldData.bio,
              'location-text': item.fieldData.locationText,
              'offering-id': item.fieldData.offeringId,
              'offering-status': item.fieldData.offeringStatus,
              'type-of-offering': item.fieldData.offeringType,
              offering: item.fieldData.offeringName,
              'offering-long-description': item.fieldData.offeringLongDescription,
              'wellbeing-capacity': item.fieldData.offeringWellbeing,
              'what-you-will-learn': item.fieldData.aboutLearning,
              'practice-benefits': item.fieldData.aboutBenefits,
              'incorporating-in-daily-life': item.fieldData.aboutDailyLife,
              'relevance-ancient': item.fieldData.relevanceAncient,
              'relevance-scientific': item.fieldData.relevanceScientific,
              'session-introductory': item.fieldData.introSessionExplainer,
              'intro-session-prerequisites': item.fieldData.introSessionPrerequisites,
              'session-full-plan': item.fieldData.sessionFullPlan,
              'session-type': item.fieldData.sessionType,
              'booking-info-1': item.fieldData.sessionPrivateSessions,
              'booking-info-2': item.fieldData.sessionDuration,
              faqs: item.fieldData.faqs,
              price: item.fieldData.price,
              'offering-type-conduct': item.fieldData.offeringTypeConduct,
              'offering-full-course-price': item.fieldData.offeringFullCoursePrice,
              'calendar-id': item.fieldData.calendarId,
              'owner-id': item.fieldData.ownerId,
              'offering-capacity-name': item.fieldData.offeringCapacityName,
              elements: item.fieldData.elements || [],
              'sub-elements': item.fieldData.subElements || [],
              'main-color': item.fieldData.mainColor,
            },
          },
        },
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      );

  if (response.status !== 200) {
    alert(`Error updating item in collection ${response}`);
  }

  dispatchNotification(
    onlyUpdateInSpreadsheet
      ? 'Skipped updating in webflow'
      : 'Specialist updated successfully to webflow',
    onlyUpdateInSpreadsheet ? 'info' : 'success'
  );

  const spreadSheetUpdateResponse = await updateSpecialistSpreadsheetItem(
    collectionId,
    itemId,
    item,
    dispatchNotification
  );

  if (spreadSheetUpdateResponse.status !== 200) {
    dispatchNotification('Error updating item in spreadsheet', 'error');
    return response.data;
  }

  return response.data;
}

export async function addSpecialistItemInCollection(
  collectionId: string,
  item: WebFlowItem<'Specialist'>
) {
  if (!access_token || isTokenExpired(access_token)) {
    refreshAuthToken();
  }

  if (!access_token) {
    alert(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com"
    );
  }

  const response = await axios.post(
    // "http://127.0.0.1:5001/subtle-cubist-419805/us-central1/handleWebflow",
    'https://handlewebflow-gqjfwgmboa-uc.a.run.app',
    {
      actionType: 'ADD_ITEM_TO_COLLECTION',
      request: {
        collectionId: collectionId,
        isDraft: true,
        fieldData: {
          name: item.fieldData.name,
          slug: item.fieldData.slug,
          'crisp-bio': item.fieldData.crispBio,
          ranking: item.fieldData.ranking,
          details: item.fieldData.bio,
          'location-text': item.fieldData.locationText,
          'offering-id': item.fieldData.offeringId,
          'offering-status': item.fieldData.offeringStatus,
          'type-of-offering': item.fieldData.offeringType,
          offering: item.fieldData.offeringName,
          'offering-long-description': item.fieldData.offeringLongDescription,
          'wellbeing-capacity': item.fieldData.offeringWellbeing,
          'what-you-will-learn': item.fieldData.aboutLearning,
          'practice-benefits': item.fieldData.aboutBenefits,
          'incorporating-in-daily-life': item.fieldData.aboutDailyLife,
          'relevance-ancient': item.fieldData.relevanceAncient,
          'relevance-scientific': item.fieldData.relevanceScientific,
          'session-introductory': item.fieldData.introSessionExplainer,
          'intro-session-prerequisites': item.fieldData.introSessionPrerequisites,
          'session-full-plan': item.fieldData.sessionFullPlan,
          'session-type': item.fieldData.sessionType,
          'booking-info-1': item.fieldData.sessionPrivateSessions,
          'booking-info-2': item.fieldData.sessionDuration,
          faqs: item.fieldData.faqs,
          price: item.fieldData.price,
          'offering-type-conduct': item.fieldData.offeringTypeConduct,
          'offering-full-course-price': item.fieldData.offeringFullCoursePrice,
          'calendar-id': item.fieldData.calendarId,
          'owner-id': item.fieldData.ownerId,
          'offering-capacity-name': item.fieldData.offeringCapacityName,
          elements: item.fieldData.elements || [],
          'sub-elements': item.fieldData.subElements || [],
        },
      },
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    alert(`Error adding item to collection ${response}`);
  }

  return response.data;
}

export async function updateExperiencesSpreadsheetItem(
  collectionId: string,
  itemId: string,
  item: WebFlowItem<'Experience'>,
  dispatchNotification: (message: string, type: ToastIntent) => void
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    alert(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leealife.com"
    );
  }
  const response = await axios.post(
    // "http://127.0.0.1:5001/subtle-cubist-419805/us-central1/handleSpreadSheetUpdate",
    'https://handlespreadsheetupdate-gqjfwgmboa-uc.a.run.app',
    {
      actionType: 'UPDATE',
      request: {
        collectionId,
        itemId,
        itemData: {
          'Offering Name': item.fieldData.name,
          Slug: item.fieldData.slug,
          'Collection ID': collectionId,
          'Item ID': itemId,
          'Experience - Description (Short)': item.fieldData.offeringLongDescription,
          'Location Text': item.fieldData.locationText,
          'What will I experience?': item.fieldData.aboutLearning,
          'Benefits of this experience': item.fieldData.aboutBenefits,
          'Incorporating in daily life': item.fieldData.aboutDailyLife,
          'About the destination': item.fieldData.destinationAboutInstitution,
          'Place of experience': item.fieldData.destinationPlace,
          'Most suitable time to visit': item.fieldData.contextTimeAndSeason,
          'Ancient texts and treatises:': item.fieldData.relevanceAncient,
          'Modern scientific researches:': item.fieldData.relevanceScientific,
          'Experience schedule': item.fieldData.sessionFullPlan,
          Availability: item.fieldData.experinceUpcoming,
          'Experience - Pricing': item.fieldData.experienceFullPrice,
          'Experience - Duration': item.fieldData.experienceDuration,
          'Coordinated by': item.fieldData.experienceByName,
          'Crisp bio - Coordinator': item.fieldData.experienceByCrispBio,
          'Calendar ID': item.fieldData.calendarId,
          'About the Coordinator': item.fieldData.aboutSpecialist,
        },
      },
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification('Error updating item in spreadsheet', 'error');
    return response;
  }

  console.log('spreadsheet update successfully', response);

  dispatchNotification('Experience updated successfully in spreadsheet', 'success');

  return response;
}

export async function updateSpecialistSpreadsheetItem(
  collectionId: string,
  itemId: string,
  item: WebFlowItem<'Specialist'>,
  dispatchNotification: (message: string, type: ToastIntent) => void
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    alert(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com"
    );
  }

  const response = await axios.post(
    // "http://127.0.0.1:5001/subtle-cubist-419805/us-central1/handleSpreadSheetUpdate",
    'https://handlespreadsheetupdate-gqjfwgmboa-uc.a.run.app',
    {
      actionType: 'UPDATE',
      request: {
        collectionId,
        itemId,
        itemData: {
          Name: item.fieldData.name,
          Slug: item.fieldData.slug,
          Ranking: item.fieldData.ranking,
          'Specialist - Crisp Bio': item.fieldData.crispBio,
          'Specialist - Bio': item.fieldData.bio,
          'Specialist - Location Text': item.fieldData.locationText,
          'Offering - Status': item.fieldData.offeringStatus,
          'Offering - Type': item.fieldData.offeringType,
          'Offering - Name': item.fieldData.offeringName,
          'Offering - Long description': item.fieldData.offeringLongDescription,
          'Offering - Wellbeing': item.fieldData.offeringWellbeing,
          'About - Learning': item.fieldData.aboutLearning,
          'About - Benefits': item.fieldData.aboutBenefits,
          'About - Daily Life': item.fieldData.aboutDailyLife,
          'Relevance - Ancient': item.fieldData.relevanceAncient,
          'Relevance - Scientific': item.fieldData.relevanceScientific,
          'Intro Session - Explainer': item.fieldData.introSessionExplainer,
          'Intro Session - Prerequisites': item.fieldData.introSessionPrerequisites,
          'Session - Full Plan': item.fieldData.sessionFullPlan,
          'Session - Type': item.fieldData.sessionType,
          'Session - Private Sessions': item.fieldData.sessionPrivateSessions,
          'Session - Duration': item.fieldData.sessionDuration,
          'Session - Frequency': item.fieldData.sessionFrequency,
          FAQs: item.fieldData.faqs,
          Price: item.fieldData.price,
          'Offering - Short description': item.fieldData.offeringShortDescription,
          'Offering - Type - Conduct': item.fieldData.offeringTypeConduct,
          'Calendar ID': item.fieldData.calendarId,
          'Owner ID': item.fieldData.ownerId,
          'Offering - Capacity Name': item.fieldData.offeringCapacityName,
        },
      },
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification('Error updating item in spreadsheet', 'error');
    return response;
  }

  console.log('spreadsheet update successfully', response);

  dispatchNotification('Specialist updated successfully in spreadsheet', 'success');

  return response;
}

export async function uploadImageToGoogleCDN(
  imageURL: string,
  fileId: string,
  dispatchNotification: (message: string, type: ToastIntent) => void
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
  }

  const response = await axios.post(
    // "http://127.0.0.1:5001/subtle-cubist-419805/us-central1/uploadToGoogleCDN",
    'https://uploadtogooglecdn-gqjfwgmboa-uc.a.run.app',
    {
      url: imageURL,
      fileName: fileId,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error uploading image to CDN ${response}`, 'error');
  }
  dispatchNotification('Image uploaded successfully to CDN', 'success');

  return response.data.mediaLinkPostUpload;
}

export async function fetchDropDownForCollection(
  collectionId: string
): Promise<LinkedDropDownObject[]> {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    alert(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com"
    );
  }

  const response = await axios.post(
    // "http://127.0.0.1:5001/subtle-cubist-419805/us-central1/handleWebflow",
    'https://handlewebflow-gqjfwgmboa-uc.a.run.app',
    {
      actionType: 'FETCH_ITEMS_FOR_DROPDOWN',
      request: {
        collectionId,
      },
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    alert(`Error fetching dropdown for collection ${response}`);
  }

  return response.data as LinkedDropDownObject[];
}

export async function addUserToFlow(
  email: string,
  firstName: string,
  lastName: string,
  discount: number,
  dispatchNotification: (message: string, type: ToastIntent) => void,
  scenarioName: FlowScenarioName,
  leadId?: string,
  guideId?: string,
  type?: number,
  isOfflinePaymentDone?: boolean
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
  }

  const response = await axios.post(
    getURLFromFunctionName('enableUserBlueprintFlowForUser'),
    {
      email,
      discount,
      firstName,
      lastName,
      flowScenario: scenarioName,
      leadId,
      guideId,
      type: type || 0,
      ...(isOfflinePaymentDone && { isOfflinePaymentDone: true }),
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error adding user to blueprint flow ${response}`, 'error');
  }

  dispatchNotification('User added to blueprint flow successfully', 'success');

  return response.data;
}

export async function fetchUserFlowStatus(
  email: string,
  dispatchNotification: (message: string, type: ToastIntent) => void,
  scenarioName: FlowScenarioName
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
  }

  const response = await axios.post(
    getURLFromFunctionName('fetchUserBlueprintFlowStatus'),
    {
      ...(email.length > 0 && { email }),
      flowScenario: scenarioName,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error fetching user blueprint flow status ${response}`, 'error');
  }

  dispatchNotification('User blueprint flow status fetched successfully', 'success');

  return response.data;
}

export async function addNewMailReportTemplate(
  templateName: string,
  design: any,
  dispatchNotification?: (message: string, type: ToastIntent) => void
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification?.(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
  }

  const response = await axios.post(
    // "http://127.0.0.1:5001/subtle-cubist-419805/us-central1/addNewMailTemplate",
    'https://addNewMailTemplate-gqjfwgmboa-uc.a.run.app',
    {
      name: templateName,
      mailTemplateDesign: design,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification?.(`Error adding new mail template ${response}`, 'error');
  }

  dispatchNotification?.('Mail template added successfully', 'success');

  return response.data;
}

export async function fetchMailTemplate(
  templateName: string,
  dispatchNotification?: (message: string, type: ToastIntent) => void
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification?.(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
  }

  const response = await axios.post(
    // "http://127.0.0.1:5001/subtle-cubist-419805/us-central1/fetchMailTemplate",
    'https://fetchMailTemplate-gqjfwgmboa-uc.a.run.app',
    {
      name: templateName,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification?.(`Error fetching mail template ${response}`, 'error');
  }

  dispatchNotification?.('Mail template fetched successfully', 'success');

  return response.data;
}

export async function fetchAllMailTemplates(
  dispatchNotification?: (message: string, type: ToastIntent) => void
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification?.(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
  }

  const response = await axios.post(
    // "http://127.0.0.1:5001/subtle-cubist-419805/us-central1/fetchAllMailTemplates",
    'https://fetchallmailtemplates-gqjfwgmboa-uc.a.run.app',
    {},
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification?.(`Error fetching all mail templates ${response}`, 'error');
    return [];
  }

  dispatchNotification?.('All mail templates fetched successfully', 'success');

  return response.data;
}

export async function updateUserReportInUserBlueprintFlow(
  email: string,
  secret: string,
  reportKey: string,
  reportIndex: number,
  file: File | string,
  flowScenario: FlowScenarioName = 'blueprint',
  dispatchNotification?: (message: string, type: ToastIntent) => void
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification?.(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
  }

  const response = await axios.post(
    // "http://127.0.0.1:5001/subtle-cubist-419805/us-central1/addUserReportToBlueprintFlow",
    // "https://addUserReportToBlueprintFlow-gqjfwgmboa-uc.a.run.app",
    getURLFromFunctionName('addUserReportToBlueprintFlow'),
    {
      email,
      secret,
      reportKey,
      reportIndex,
      file,
      flowScenario,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'content-type': 'multipart/form-data',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification?.(`Error updating user report in blueprint flow ${response}`, 'error');
  }

  dispatchNotification?.('User report updated successfully', 'success');

  return response.data;
}

export async function enableFeedbackForUserBlueprintFlow(
  email: string,
  flowScenario: FlowScenarioName = 'blueprint',
  dispatchNotification?: (message: string, type: ToastIntent) => void,
  action?: 'activated' | 'conversation_completed',
  followUpNumber: number = 1
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification?.(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );

    return;
  }

  const response = await axios.post(
    getURLFromFunctionName('enableFeedbackFlowForUser'),
    {
      email,
      flowScenario,
      action,
      followUpNumber,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification?.(`Error enabling feedback for user blueprint flow ${response}`, 'error');
  }

  dispatchNotification?.('Feedback enabled successfully', 'success');

  return response.data;
}

export async function markConversationAsCompleted(
  email: string,
  flowScenario: FlowScenarioName = 'blueprint',
  isFeedbackFlow: boolean = false,
  dispatchNotification?: (message: string, type: ToastIntent) => void,
  followUpNumber: number = 1
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification?.(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
    return;
  }

  const response = await axios.post(
    getURLFromFunctionName('markConversationAsCompleted'),
    {
      email,
      flowScenario,
      isFeedbackFlow,
      followUpNumber,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification?.(`Error marking conversation as completed ${response}`, 'error');
    return;
  }

  dispatchNotification?.(
    'Conversation marked as completed successfully. Please refresh the list to view updated changes',
    'success'
  );

  return response.data;
}

export async function modifyUserEmailInFlow(
  email: string,
  newEmail: string,
  dispatchNotification?: (message: string, type: ToastIntent) => void,
  flowScenario: FlowScenarioName = 'blueprint'
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification?.(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
    return;
  }

  const response = await axios.post(
    getURLFromFunctionName('modifyUserEmailInBlueprintFlow'),
    {
      email,
      newEmail,
      flowScenario,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification?.(`Error modifying user email in flow ${response}`, 'error');
  }

  dispatchNotification?.(
    'User email modified successfully. Please refresh the list to view updated changes',
    'success'
  );

  return response.data;
}

export async function skipPaymentForUser(
  email: string,
  dispatchNotification?: (message: string, type: ToastIntent) => void,
  flowScenario: FlowScenarioName = 'blueprint'
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification?.(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
    return;
  }

  const response = await axios.post(
    getURLFromFunctionName('skipPaymentForUser'),
    {
      email,
      flowScenario,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification?.(`Error skipping payment for user ${response}`, 'error');
    return;
  }

  dispatchNotification?.(
    'Payment skipped successfully. Please refresh the list to view updated changes',
    'success'
  );

  return response.data;
}

export async function getFirebaseUidByEmail(
  email: string,
  dispatchNotification?: (message: string, type: ToastIntent) => void
): Promise<{ success: boolean; uid: string } | undefined> {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification?.(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
    return;
  }

  const response = await axios.post(
    getURLFromFunctionName('getFirebaseUidByEmail'),
    { email },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification?.('Error looking up Firebase UID', 'error');
    return;
  }

  return response.data;
}

export async function enablePathAndCircleForUser(
  email: string,
  dispatchNotification?: (message: string, type: ToastIntent) => void,
  flowScenario: FlowScenarioName = 'blueprint'
): Promise<{ success: boolean } | undefined> {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification?.(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
    return;
  }

  const response = await axios.post(
    getURLFromFunctionName('enablePathAndCircleForUser'),
    {
      email,
      flowScenario,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification?.(`Error enabling Path & Circle for user ${response}`, 'error');
    return;
  }

  dispatchNotification?.(
    'Path & Circle enabled successfully.',
    'success'
  );

  return response.data;
}

export async function fetchPathAndCircleClients(
  dispatchNotification?: (message: string, type: ToastIntent) => void
): Promise<{ id: string; displayName: string }[] | undefined> {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification?.(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
    return;
  }

  const response = await axios.get(
    getURLFromFunctionName('getPathAndCircleClients'),
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    }
  );

  if (response.status !== 200 || !response.data.success) {
    dispatchNotification?.('Error fetching Path & Circle clients', 'error');
    return;
  }

  return response.data.clients;
}

export async function createPathAndCircleUidMapping(
  uid: string,
  clientId: string,
  dispatchNotification?: (message: string, type: ToastIntent) => void
): Promise<boolean> {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification?.(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
    return false;
  }

  const response = await axios.post(
    getURLFromFunctionName('createPathAndCircleUidMapping'),
    { uid, clientId },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (response.status !== 200 || !response.data.success) {
    dispatchNotification?.('Error creating UID mapping', 'error');
    return false;
  }

  dispatchNotification?.('UID mapping created successfully', 'success');
  return true;
}

export async function fetchPathAndCircleUidMappings(
  dispatchNotification?: (message: string, type: ToastIntent) => void
): Promise<{ uid: string; clientId: string }[] | undefined> {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification?.(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
    return;
  }

  const response = await axios.get(
    getURLFromFunctionName('getPathAndCircleUidMappings'),
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    }
  );

  if (response.status !== 200 || !response.data.success) {
    dispatchNotification?.('Error fetching UID mappings', 'error');
    return;
  }

  return response.data.mappings;
}

export async function deletePathAndCircleUidMapping(
  uid: string,
  dispatchNotification?: (message: string, type: ToastIntent) => void
): Promise<boolean> {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification?.(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
    return false;
  }

  const response = await axios.post(
    getURLFromFunctionName('deletePathAndCircleUidMapping'),
    { uid },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (response.status !== 200 || !response.data.success) {
    dispatchNotification?.('Error deleting UID mapping', 'error');
    return false;
  }

  dispatchNotification?.('UID mapping deleted successfully', 'success');
  return true;
}

export async function fetchAllLeads(
  leadType: string,
  dispatchNotification?: (message: string, type: ToastIntent) => void
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification?.(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
    return;
  }

  const response = await axios.post(
    // "http://127.0.0.1:5001/subtle-cubist-419805/us-central1/fetchAllLeads",
    'https://fetchallleads-gqjfwgmboa-uc.a.run.app',
    {
      type: leadType,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification?.(`Error fetching all leads ${response}`, 'error');
    return [];
  }

  dispatchNotification?.('All leads fetched successfully', 'success');

  return response.data;
}

export async function enableEnableFormOverride(
  email: string,
  dispatchNotification: (message: string, type: ToastIntent) => void,
  scenarioName: FlowScenarioName
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );

    return;
  }

  const response = await axios.post(
    // "http://127.0.0.1:5001/subtle-cubist-419805/us-central1/enableformoverrideforuser",
    'https://enableformoverrideforuser-gqjfwgmboa-uc.a.run.app',
    {
      email,
      flowScenario: scenarioName,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error enabling form override for user ${response}`, 'error');
  }

  dispatchNotification('Form override enabled successfully', 'success');

  return response.data;
}

export async function fetchAllGuides(
  dispatchNotification: (message: string, type: ToastIntent) => void
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );

    return;
  }

  const response = await axios.post(
    getURLFromFunctionName('fetchAllGuides'),
    {},
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error fetching all guides ${response}`, 'error');
    return [];
  }

  dispatchNotification('All guides fetched successfully', 'success');

  return response.data;
}

export async function fetchAllAvailableGuides(
  dispatchNotification: (message: string, type: ToastIntent) => void
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelaife.com",
      'error'
    );

    return;
  }

  const response = await axios.post(
    getURLFromFunctionName('fetchAllAvailableGuides'),
    {},
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error fetching all available guides ${response}`, 'error');
    return [];
  }

  dispatchNotification('All available guides fetched successfully', 'success');

  return response.data;
}

export async function portGuideFromAcuity(
  guideId: string,
  dispatchNotification: (message: string, type: ToastIntent) => void
): Promise<void> {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
  }

  const response = await axios.post(
    getURLFromFunctionName('portGuideFromAcuity'),
    {
      guideId,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  console.log(response);

  if (response.status !== 200) {
    dispatchNotification(`Error porting guide from acuity ${response}`, 'error');
    return;
  }

  dispatchNotification('Guide ported successfully', 'success');

  return;
}

export async function generateCouponCode(
  discount: number,
  dispatchNotification: (message: string, type: ToastIntent) => void
): Promise<string> {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );

    return '';
  }

  const response = await axios.post(
    getURLFromFunctionName('generateStripeCouponCode'),
    {
      discount,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error generating coupon code ${response}`, 'error');
    return '';
  }

  dispatchNotification('Coupon code generated successfully', 'success');

  return response.data.couponId as string;
}

export async function getImageFromDriveLink(
  link: string,
  dispatchNotification: dispatchNotification
): Promise<string> {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );

    return '';
  }

  try {
    const response = await axios.post(
      getURLFromFunctionName('getImageForDriveLink'),
      {
        url: link,
      },
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );

    if (response.status !== 200) {
      dispatchNotification(`Error fetching image from drive link ${response}`, 'error');
      return '';
    }

    dispatchNotification('Image fetched successfully', 'success', 5000);

    return response.data.dataImageURL;
  } catch (error) {
    dispatchNotification(`Error fetching image from drive link ${error}`, 'error');
    return '';
  }
}

export async function saveReportAsDraft(
  email: string,
  reportProps: ReportProps,
  dispatchNotification: dispatchNotification,
  flowScenario: FlowScenarioName = 'blueprint'
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );

    return '';
  }

  const response = await axios.post(
    getURLFromFunctionName('saveReportAsDraft'),
    {
      email,
      reportProps,
      flowScenario,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error saving report as draft ${response}`, 'error', 5000);
    return '';
  }

  dispatchNotification('Report saved as draft successfully', 'success', 5000);

  return response.data;
}

export async function loadUserReport(
  email: string,
  dispatchNotification: dispatchNotification,
  flowScenario: FlowScenarioName = 'blueprint'
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );

    return '';
  }

  const response = await axios.post(
    getURLFromFunctionName('loadUserReport'),
    {
      email,
      flowScenario,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error loading user report ${response}`, 'error', 5000);
    return '';
  }

  dispatchNotification('User report loaded successfully', 'success', 5000);

  return response.data;
}

export async function publishUserReport(
  email: string,
  file: File,
  flowScenario: FlowScenarioName = 'blueprint',
  // reportUrl: string,
  dispatchNotification: dispatchNotification
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );

    return '';
  }

  const response = await axios.post(
    getURLFromFunctionName('publishUserReport'),
    {
      email,
      file,
      flowScenario,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'content-type': 'multipart/form-data',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error publishing user report ${response}`, 'error', 5000);
    return '';
  }

  dispatchNotification('User report published successfully', 'success', 5000);

  return response.data;
}

// Publish report by breaking it into chunks of max 8MB each
// then finally invoking the publish functionality
export async function publishUserReportV2(
  email: string,
  file: File,
  flowScenario: FlowScenarioName = 'blueprint',
  dispatchNotification: dispatchNotification
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );

    return '';
  }
  // get file size
  const fileSize = file.size;

  const chunkSize = 8 * 1024 * 1024; // 8MB
  const totalChunks = Math.ceil(fileSize / chunkSize);

  // const chunks: Blob[] = [];
  for (let i = 0; i < totalChunks; i++) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, fileSize);
    const chunk = file.slice(start, end);
    // upload chunk to the server
    dispatchNotification(`Uploading chunk ${i + 1} of ${totalChunks}`, 'info', 3000);
    if (!access_token || isTokenExpired(access_token)) {
      await refreshAuthToken();
    }
    const response = await axios.post(
      getURLFromFunctionName('uploadPartialFileChunkForUserReport'),
      {
        email,
        file: chunk,
        partNumber: i + 1,
        totalParts: totalChunks,
        flowScenario,
      },
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
          'content-type': 'multipart/form-data',
          Accept: 'application/json',
        },
      }
    );

    if (response.status !== 200) {
      dispatchNotification(`Error uploading chunk ${i + 1} of ${totalChunks}`, 'error', 3000);
      return '';
    } else {
      dispatchNotification(
        `Chunk ${i + 1} of ${totalChunks} uploaded successfully`,
        'success',
        3000
      );
    }
  }

  // check access token again
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  const response = await axios.post(
    getURLFromFunctionName('combineChunksAndPublishReport'),
    {
      email,
      flowScenario,
      totalParts: totalChunks,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error publishing user report ${response}`, 'error', 5000);
    return '';
  }

  dispatchNotification('User report published successfully', 'success', 5000);
  return response.data;
}

export async function saveReportAsDraftV2(
  email: string,
  reportProps: ReportProps,
  phase: string,
  dispatchNotification: dispatchNotification,
  flowScenario: FlowScenarioName = 'blueprint'
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );

    return '';
  }

  const response = await axios.post(
    getURLFromFunctionName('saveReportAsDraftV2'),
    {
      email,
      reportProps,
      phase,
      flowScenario,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error saving phased report as draft ${response}`, 'error', 5000);
    return '';
  }

  dispatchNotification('Phased report saved as draft successfully', 'success', 5000);

  return response.data;
}

export async function loadUserReportV2(
  email: string,
  isPublished: boolean,
  dispatchNotification: dispatchNotification,
  flowScenario: FlowScenarioName = 'blueprint'
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );

    return '';
  }

  const response = await axios.post(
    getURLFromFunctionName('loadUserReportV2'),
    {
      email,
      isPublished,
      flowScenario,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error loading phased user report ${response}`, 'error', 5000);
    return '';
  }

  const reportType = isPublished ? 'published' : 'draft';
  dispatchNotification(`Phased ${reportType} report loaded successfully`, 'success', 5000);

  return response.data;
}

export async function getDownloadableUserReportLink(
  email: string,
  dispatchNotification: dispatchNotification,
  flowScenario: FlowScenarioName = 'blueprint'
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );

    return '';
  }

  const response = await axios.post(
    getURLFromFunctionName('getDownloadableUserReportLink'),
    {
      email,
      flowScenario,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error fetching downloadable user report link ${response}`, 'error', 5000);
    return '';
  }
  dispatchNotification('Downloadable user report link fetched successfully', 'success', 5000);

  return response.data.downloadLink[0];
}

export async function publishPhasedUserReport(
  email: string,
  dispatchNotification: dispatchNotification,
  flowScenario: FlowScenarioName = 'blueprint'
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );

    return '';
  }

  const response = await axios.post(
    getURLFromFunctionName('publishUserReportV2'),
    {
      email,
      flowScenario,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error publishing phased user report ${response}`, 'error', 5000);
    return '';
  }

  dispatchNotification('Phased report published successfully', 'success', 5000);

  return response.data;
}

export async function reset2ndConversation(
  email: string,
  dispatchNotification: dispatchNotification
): Promise<boolean> {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );

    return false;
  }

  try {
    const response = await axios.post(
      getURLFromFunctionName('reset2ndConversation'),
      {
        email,
        flowScenario: 'blueprint',
      },
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );

    console.log(response.status, response.data);

    if (response.status !== 200) {
      dispatchNotification(`Error resetting 2nd conversation: ${response.data.error}`, 'error');
      return false;
    }
    dispatchNotification('2nd conversation reset successfully', 'success');
    return response.data.success as boolean;
  } catch (error: any) {
    console.error('Error resetting 2nd conversation:', error.response.data.error);
    dispatchNotification(`Error resetting 2nd conversation: ${error.response.data.error}`, 'error');
    return false;
  }
}

export async function resetClientConversation(
  email: string,
  flowScenario: FlowScenarioName = 'blueprint',
  isFeedbackFlow: boolean = false,
  dispatchNotification?: (message: string, type: ToastIntent) => void,
  followUpNumber: number = 1
): Promise<boolean> {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification?.(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
    return false;
  }

  try {
    const response = await axios.post(
      getURLFromFunctionName('resetClientConversation'),
      {
        email,
        flowScenario,
        isFeedbackFlow,
        followUpNumber,
      },
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );

    if (response.status !== 200) {
      dispatchNotification?.(
        `Error resetting client conversation: ${response.data?.error || 'Unknown error'}`,
        'error'
      );
      return false;
    }

    dispatchNotification?.(
      `Follow-up #${followUpNumber} conversation reset successfully. Please refresh the list to view updated changes`,
      'success'
    );
    return response.data.success as boolean;
  } catch (error: any) {
    console.error(
      'Error resetting client conversation:',
      error.response?.data?.error || error.message
    );
    dispatchNotification?.(
      `Error resetting client conversation: ${error.response?.data?.error || error.message}`,
      'error'
    );
    return false;
  }
}

export async function setUserRole(
  email: string,
  roleType: RoleType,
  dispatchNotification: dispatchNotification
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );

    return;
  }

  const response = await axios.post(
    getURLFromFunctionName('setUserRole'),
    {
      email,
      roleType,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error setting user role ${response}`, 'error');
    return;
  }

  dispatchNotification('User role set successfully', 'success');
  return response.data;
}

export async function fetchAllPermissions(): Promise<string[]> {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    alert(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com"
    );
    return [];
  }

  try {
    const response = await axios.post(
      getURLFromFunctionName('getUserPermissions'),
      {},
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );

    if (response.status !== 200) {
      alert(`Error fetching permissions: ${response.data.error}`);
      return [];
    }

    return response.data.permissions as string[];
  } catch (error: any) {
    if (
      error.response?.status === 403 &&
      error.response?.data?.error === 'Domain access restricted'
    ) {
      throw new Error('DOMAIN_ACCESS_RESTRICTED');
    }
    alert(`Error fetching permissions: ${error.response?.data?.message || error.message}`);
    return [];
  }
}

export async function fetchAllUsers(
  dispatchNotification: dispatchNotification
): Promise<{ email: string; role: RoleType; name?: string }[]> {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
    return [];
  }

  const response = await axios.post(
    getURLFromFunctionName('getAllUsersWithRole'),
    {},
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error fetching all users: ${response.data.error}`, 'error');
    return [];
  }

  console.log('All users fetched successfully', response);

  dispatchNotification('All users fetched successfully', 'success');
  return response.data.allUsers as { email: string; role: RoleType; name?: string }[];
}

export async function getAssessmentDetails(
  userEmail: string,
  flowScenario: FlowScenarioName = 'blueprint',
  dispatchNotification: dispatchNotification
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
    return;
  }

  const response = await axios.post(
    getURLFromFunctionName('getAssessmentDetails'),
    {
      userEmail,
      flowScenario,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error fetching assessment details: ${response.data.error}`, 'error');
    return;
  }

  dispatchNotification('Assessment details fetched successfully', 'success');
  console.log('Assessment details fetched successfully', response.data);
  return response.data;
}

export async function setUserInnateQualities(
  userEmail: string,
  primaryQualities: InnateQualities[],
  unclearQualities: InnateQualities[],
  flowScenario: FlowScenarioName = 'blueprint',
  dispatchNotification: dispatchNotification
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
    return;
  }

  const response = await axios.post(
    getURLFromFunctionName('setUserInnateQualities'),
    {
      userEmail,
      primaryQualities,
      unclearQualities,
      flowScenario,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error setting user innate qualities: ${response.data.error}`, 'error');
    return;
  }

  dispatchNotification('User innate qualities set successfully', 'success');
  console.log('User innate qualities set successfully', response.data);
  return response.data;
}

export async function setAssessmentDetails(
  userEmail: string,
  clientAssessment: string,
  talkingPoints: string,
  flowScenario: FlowScenarioName = 'blueprint',
  dispatchNotification: dispatchNotification
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
    return;
  }

  const response = await axios.post(
    getURLFromFunctionName('setQuestionnaireDetails'),
    {
      userEmail,
      clientAssessment,
      talkingPoints,
      flowScenario,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error saving assessment details: ${response.data.error}`, 'error');
    return;
  }

  dispatchNotification('Assessment details saved successfully', 'success');
  return response.data;
}

export async function postAssessmentComment(
  userEmail: string,
  flowScenario: FlowScenarioName = 'blueprint',
  comment: string,
  dispatchNotification: dispatchNotification
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
    return;
  }

  const response = await axios.post(
    getURLFromFunctionName('postAssessmentComment'),
    {
      userEmail,
      flowScenario,
      messageBody: comment,
      timeStamp: new Date().toISOString(),
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error posting comment: ${response.data.error}`, 'error');
    return;
  }

  dispatchNotification('Comment posted successfully', 'success');
  return response.data;
}

export async function getAssessmentComments(
  userEmail: string,
  flowScenario: FlowScenarioName = 'blueprint',
  dispatchNotification: dispatchNotification
) {
  if (!access_token || isTokenExpired(access_token)) {
    await refreshAuthToken();
  }

  if (!access_token) {
    dispatchNotification(
      "Error getting access token, Please logout and login again. If it still doesn't work, please reach out to ishantdeep@leelalife.com",
      'error'
    );
    return [];
  }

  const response = await axios.post(
    getURLFromFunctionName('getAssessmentComments'),
    {
      userEmail,
      flowScenario,
    },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  if (response.status !== 200) {
    dispatchNotification(`Error fetching comments: ${response.data.error}`, 'error');
    return [];
  }

  return response.data.comments || [];
}

// ============================================================================
// PROMPT RESOLUTION UTILITIES
// ============================================================================
export {
  resolvePrompt,
  resolvePromptPlaceholders,
  replaceDataPlaceholders,
  extractUserDataFromAssessment,
} from './utils/promptResolver';
